import { forwardRef } from "react";
import { NodeViewComponentProps } from "@handlewithcare/react-prosemirror";
import useSingleContent from "@/content/useSingleContentQuery/useSingleContentQuery";
import ContentRow from "@/components/ContentRow";
import ContentCardV2 from "@/components/ContentCardV2";
import { ContentRowProps } from "@/components/ContentRow/ContentRow.interface";
import {
  CardViewmodelAndProps,
  ContentTypeEnum,
} from "@/components/ContentCardV2/ContentCardV2.interface";
import { useBundleDataContext } from "@/bundles/providers/useBundleDataContext";
import findCurrentContentIdTreeRoute from "@/bundles/utils/findCurrentContentIdTreeRoute";

type ViewmodelProps = {
  flavor: string;
};

const Viewmodel = forwardRef<
  HTMLDivElement,
  NodeViewComponentProps & ViewmodelProps
>(({ nodeProps, flavor }, ref) => {
  const nodeType = nodeProps.node.attrs["type"];
  const nodeId = nodeProps.node.attrs["id"];
  const { contentId: rootContentId, contentOutline } = useBundleDataContext();

  const { data, isLoading } = useSingleContent<
    ContentRowProps | CardViewmodelAndProps
  >({
    contentId: nodeId,
    queryParams: { viewmodel_type: nodeType },
  });

  /**
   * Constructs bundle navigation URLs using the tree outline as source of truth.
   * This ensures bundle cards navigate with the full hierarchical path, preserving
   * the correct parent/child context in the tree navigation, for those cases in which
   * nested bundles belong to multiple parents.
   */
  const constructBundleUrl = (
    childId: string,
    originalUrl: string,
    contentType?: ContentTypeEnum,
  ) => {
    if (contentType !== ContentTypeEnum.BUNDLE) {
      return originalUrl;
    }
    if (contentOutline?.items) {
      const treePath = findCurrentContentIdTreeRoute(
        contentOutline.items,
        childId,
      );
      if (treePath) {
        return `/content/subject/${rootContentId}?tree=${encodeURIComponent(treePath)}`;
      }
    }

    return originalUrl;
  };

  const getDataWithCorrectUrls = () => {
    if (!data || !rootContentId) return data;

    if (nodeType === "card") {
      const cardData = data as CardViewmodelAndProps;
      return {
        ...cardData,
        url: constructBundleUrl(
          cardData.id,
          cardData.url,
          cardData.metadata?.content_type,
        ),
      };
    }

    if (nodeType === "row") {
      const rowData = data as ContentRowProps;
      return {
        ...rowData,
        children: rowData.children.map((child) => ({
          ...child,
          url: constructBundleUrl(
            child.id,
            child.url,
            child.metadata?.content_type,
          ),
        })),
      };
    }

    return data;
  };

  const dataWithCorrectUrls = getDataWithCorrectUrls();

  return (
    /**
     * This outer <div ref={ref}> is the *nodeview root* that ProseMirror
     * attaches to the document. ProseMirror relies on this stable root to
     * map between DOM and document positions. It must:
     *   - Always be present (even if contents are empty)
     *   - Always have the `ref` attached
     */
    <div
      ref={ref}
      onMouseDownCapture={(e) => {
        // Prevent ProseMirror from interpreting clicks on this node’s UI
        // as selection events. Especially important once viewmodels become
        // draggable/editable, since those interactions should be handled by
        // our own logic, not by PM’s selection system.
        e.stopPropagation();
      }}
    >
      {!isLoading && !dataWithCorrectUrls && null}

      {!isLoading && dataWithCorrectUrls && (
        <>
          {nodeType === "row" && (
            <ContentRow
              {...(dataWithCorrectUrls as ContentRowProps)}
              flavor={flavor}
            />
          )}
          {nodeType === "card" && (
            <ContentCardV2
              {...(dataWithCorrectUrls as CardViewmodelAndProps)}
            />
          )}
        </>
      )}
    </div>
  );
});

export default Viewmodel;
